import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TableLazyLoadEvent, TableRowExpandEvent } from 'primeng/table';
import { Observable, Subject, Subscription, debounceTime, finalize, forkJoin } from 'rxjs';
import { MyOskLectureGroupsService } from '../../../../api/api-services/my-osk-lecture-groups.service';
import { MyOskLectureGroupService } from '../../../../api/api-services/my-osk-lecture-group.service';
import { MyOskLectureGroupArchiveService } from '../../../../api/api-services/my-osk-lecture-group-archive.service';
import { MyOskLectureGroupMembersService } from '../../../../api/api-services/my-osk-lecture-group-members.service';
import { MyOskLectureGroupMemberService } from '../../../../api/api-services/my-osk-lecture-group-member.service';
import { IMyOskLectureGroupVm } from '../../../../api/my-osk-lecture-group-vm.model';
import { IMyOskLectureGroupMemberVm } from '../../../../api/my-osk-lecture-group-member-vm.model';
import { IMyOskStudentVm } from '../../../../api/my-osk-student-vm.model';
import { SortOrder } from '../../../../api/sort-order.model';
import { centerDynamicModalConfig, rightDynamicModalConfig } from '../../../shared/helpers/modal.config';
import { getSubscriptionStatus } from '../../../shared/helpers/subscription-status';
import { eModalRoles } from '../../../shared/models/modals';
import { InternalExamDialogComponent } from '../../students/internal-exam/internal-exam-dialog.component';
import { StudentsDetailsComponent } from '../../students/students-details/students-details.component';
import { LectureGroupFormComponent } from '../lecture-group-form/lecture-group-form.component';
import { LectureGroupAddStudentsComponent } from '../lecture-group-add-students/lecture-group-add-students.component';
import {
  LectureAttendanceDialogComponent,
  LectureAttendanceDialogData,
} from '../lecture-attendance/lecture-attendance-dialog.component';
import { canAdministerStudents, canRunLectures } from '../../../shared/helpers/osk-staff-role';

/** Backend sort keys accepted by GET /MyOskLectureGroups; other columns are computed and not sortable. */
const SORTABLE_COLUMNS = new Set(['name', 'created', 'examName']);

@Component({
  selector: 'app-lecture-groups-list',
  templateUrl: './lecture-groups-list.component.html',
  styleUrl: './lecture-groups-list.component.scss',
})
export class LectureGroupsListComponent implements OnInit, OnDestroy {
  /** Editing, archiving, deleting and members - the boss and the office. */
  public readonly canManageGroups = canAdministerStudents();
  /** Starting the lectures of a group - the boss and the instructor or lecturer who leads it. */
  public readonly canRunLectures = canRunLectures();

  @Input() archived = false;
  @Input() resetTable?: Observable<void>;

  private readonly myOskLectureGroupsService = inject(MyOskLectureGroupsService);
  private readonly myOskLectureGroupService = inject(MyOskLectureGroupService);
  private readonly myOskLectureGroupArchiveService = inject(MyOskLectureGroupArchiveService);
  private readonly myOskLectureGroupMembersService = inject(MyOskLectureGroupMembersService);
  private readonly myOskLectureGroupMemberService = inject(MyOskLectureGroupMemberService);
  private readonly primengDialogService = inject(DialogService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);

  public groups: IMyOskLectureGroupVm[] = [];
  public totalRecords = 0;
  public loading = false;
  public pageSize = 10;
  public first = 0;
  public selectedGroups: IMyOskLectureGroupVm[] = [];
  public expandedRowKeys: { [guid: string]: boolean } = {};
  public membersByGroup: { [guid: string]: IMyOskLectureGroupMemberVm[] } = {};
  public membersLoading: { [guid: string]: boolean } = {};
  public bulkProcessing = false;

  private searchQuery = '';
  private readonly searchSubject = new Subject<string>();
  private lastLazyLoadEvent: TableLazyLoadEvent = { first: 0, rows: this.pageSize };
  private readonly subscriptions = new Subscription();
  private ref: DynamicDialogRef | undefined;

  ngOnInit(): void {
    this.subscriptions.add(
      this.searchSubject.pipe(debounceTime(300)).subscribe((query) => {
        this.searchQuery = query.trim().toLowerCase();
        this.first = 0;
        this.loadGroups({ ...this.lastLazyLoadEvent, first: 0 });
      })
    );
    if (this.resetTable) {
      this.subscriptions.add(
        this.resetTable.subscribe(() => {
          this.first = 0;
          this.loadGroups({ ...this.lastLazyLoadEvent, first: 0 });
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.ref?.close();
  }

  loadGroups(event: TableLazyLoadEvent): void {
    this.lastLazyLoadEvent = event;
    const rows = event.rows ?? this.pageSize;
    const page = Math.floor((event.first ?? 0) / rows) + 1;
    const sortField = Array.isArray(event.sortField) ? event.sortField[0] : event.sortField;
    const sortColumn = sortField && SORTABLE_COLUMNS.has(sortField) ? sortField : 'created';
    const sortOrder = event.sortOrder === 1 ? SortOrder.Ascending : SortOrder.Descending;

    this.loading = true;
    this.myOskLectureGroupsService
      .get(this.archived, this.searchQuery, sortColumn, page, rows, sortOrder)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (result) => {
          this.groups = result.data ?? [];
          this.totalRecords = result.allElementsCount;
          this.selectedGroups = this.selectedGroups.filter((selected) =>
            this.groups.some((group) => group.guid === selected.guid)
          );
          // Members of a group that stays expanded across a reload are re-read - the counts could have changed.
          Object.keys(this.expandedRowKeys)
            .filter((guid) => this.groups.some((group) => group.guid === guid))
            .forEach((guid) => this.loadMembers(guid));
        },
        error: () => this.showError('Nie udało się pobrać listy grup.'),
      });
  }

  refresh(): void {
    this.loadGroups(this.lastLazyLoadEvent);
  }

  onSearch(event: Event): void {
    this.searchSubject.next((event.target as HTMLInputElement).value ?? '');
  }

  onRowExpand(event: TableRowExpandEvent): void {
    const group = event.data as IMyOskLectureGroupVm;
    this.loadMembers(group.guid);
  }

  getStatus(subscriptionDate?: Date): { status: string; color: string } {
    return getSubscriptionStatus(subscriptionDate);
  }

  progressTooltip(group: IMyOskLectureGroupVm): string {
    return `Ukończone rozdziały: ${group.lecturesCompletedChapters} z ${group.lecturesTotalChapters}, wyświetlone slajdy: ${group.lecturesViewedSlides} z ${group.lecturesTotalSlides} (${group.lecturesProgressPercent}%)`;
  }

  lecturesActionLabel(group: IMyOskLectureGroupVm): string {
    return group.lecturesViewedSlides > 0 ? 'Kontynuuj wykłady' : 'Rozpocznij wykłady';
  }

  /** Sends the lectures of the group to an exam station (or to this computer). */
  startLectures(group: IMyOskLectureGroupVm): void {
    this.ref = this.primengDialogService.open(InternalExamDialogComponent, {
      ...centerDynamicModalConfig,
      width: '980px',
      showHeader: true,
      header: 'Rozpocznij wykłady',
      data: {
        lectureGroupGuid: group.guid,
        lectureGroupName: group.name,
        studentDisplayName: group.name,
        mode: 'lectures',
      },
    });
  }

  /** Attendance at the lectures of the group: the lecture days and the list of each day. */
  openAttendance(group: IMyOskLectureGroupVm): void {
    this.ref = this.primengDialogService.open(LectureAttendanceDialogComponent, {
      ...centerDynamicModalConfig,
      width: '980px',
      data: { lectureGroupGuid: group.guid, lectureGroupName: group.name } satisfies LectureAttendanceDialogData,
    });
  }

  /** Archived groups are read only - the dialog shows the group and offers to restore it. */
  showDetails(group: IMyOskLectureGroupVm): void {
    this.openFormDialog(group, true);
  }

  editGroup(group: IMyOskLectureGroupVm): void {
    this.openFormDialog(group, false);
  }

  addStudents(group: IMyOskLectureGroupVm): void {
    this.ref = this.primengDialogService.open(LectureGroupAddStudentsComponent, {
      ...centerDynamicModalConfig,
      width: '966px',
      data: { group },
    });
    this.ref.onClose.subscribe((result) => {
      if (result === eModalRoles.SUCCESS) {
        this.refresh();
      }
    });
  }

  deleteGroup(group: IMyOskLectureGroupVm): void {
    this.confirmationService.confirm({
      key: 'lectureGroupConfirm',
      header: 'Usuwanie grupy',
      message: `Czy na pewno chcesz usunąć grupę „${group.name}”? Przypisania kursantów i postęp wykładów grupy zostaną usunięte.`,
      acceptLabel: 'Usuń',
      rejectLabel: 'Anuluj',
      defaultFocus: 'reject',
      accept: () =>
        this.myOskLectureGroupService.delete(group.guid).subscribe({
          next: () => {
            this.messageService.add({ severity: 'success', detail: 'Grupa została usunięta.' });
            this.refresh();
          },
          error: (error: HttpErrorResponse) => this.showError(this.readApiError(error, 'Nie udało się usunąć grupy.')),
        }),
    });
  }

  setArchived(group: IMyOskLectureGroupVm, isArchived: boolean): void {
    this.myOskLectureGroupArchiveService.put({ guid: group.guid, isArchived }).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          detail: isArchived ? 'Grupa została zarchiwizowana.' : 'Grupa została przywrócona.',
        });
        this.refresh();
      },
      error: (error: HttpErrorResponse) =>
        this.showError(this.readApiError(error, 'Nie udało się zmienić statusu grupy.')),
    });
  }

  setArchivedSelected(isArchived: boolean): void {
    const selected = [...this.selectedGroups];
    if (selected.length === 0 || this.bulkProcessing) {
      return;
    }
    this.bulkProcessing = true;
    forkJoin(selected.map((group) => this.myOskLectureGroupArchiveService.put({ guid: group.guid, isArchived })))
      .pipe(finalize(() => (this.bulkProcessing = false)))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            detail: isArchived
              ? `Zarchiwizowano grupy: ${selected.length}.`
              : `Przywrócono grupy: ${selected.length}.`,
          });
          this.selectedGroups = [];
          this.refresh();
        },
        error: (error: HttpErrorResponse) => {
          this.showError(this.readApiError(error, 'Nie udało się zmienić statusu wszystkich zaznaczonych grup.'));
          this.refresh();
        },
      });
  }

  deleteSelected(): void {
    const selected = [...this.selectedGroups];
    if (selected.length === 0 || this.bulkProcessing) {
      return;
    }
    this.confirmationService.confirm({
      key: 'lectureGroupConfirm',
      header: 'Usuwanie grup',
      message: `Czy na pewno chcesz usunąć zaznaczone grupy (${selected.length})? Przypisania kursantów i postęp wykładów tych grup zostaną usunięte.`,
      acceptLabel: 'Usuń',
      rejectLabel: 'Anuluj',
      defaultFocus: 'reject',
      accept: () => {
        this.bulkProcessing = true;
        forkJoin(selected.map((group) => this.myOskLectureGroupService.delete(group.guid)))
          .pipe(finalize(() => (this.bulkProcessing = false)))
          .subscribe({
            next: () => {
              this.messageService.add({ severity: 'success', detail: `Usunięto grupy: ${selected.length}.` });
              this.selectedGroups = [];
              this.refresh();
            },
            error: (error: HttpErrorResponse) => {
              this.showError(this.readApiError(error, 'Nie udało się usunąć wszystkich zaznaczonych grup.'));
              this.refresh();
            },
          });
      },
    });
  }

  /** Opens the student details drawer; the arrows inside browse the members of this group. */
  showMemberDetails(group: IMyOskLectureGroupVm, member: IMyOskLectureGroupMemberVm): void {
    const members = this.membersByGroup[group.guid] ?? [];
    const currentStudentIndex = Math.max(
      0,
      members.findIndex((item) => item.guid === member.guid)
    );
    this.ref = this.primengDialogService.open(StudentsDetailsComponent, {
      ...rightDynamicModalConfig,
      data: {
        currentStudentIndex,
        // The details drawer only reads the guid and the creation date of the list entries.
        studentListPerPage: members.map(
          (item) =>
            ({
              guid: item.guid,
              displayName: item.displayName,
              created: item.created,
              email: item.email,
              validTo: item.validTo,
            }) as IMyOskStudentVm
        ),
      },
    });
    this.ref.onClose.subscribe(() => this.loadMembers(group.guid));
  }

  removeMember(group: IMyOskLectureGroupVm, member: IMyOskLectureGroupMemberVm): void {
    this.confirmationService.confirm({
      key: 'lectureGroupConfirm',
      header: 'Usuwanie z grupy',
      message: `Czy na pewno chcesz usunąć kursanta ${member.displayName} z grupy „${group.name}”?`,
      acceptLabel: 'Usuń z grupy',
      rejectLabel: 'Anuluj',
      defaultFocus: 'reject',
      accept: () =>
        this.myOskLectureGroupMemberService.delete(group.guid, member.guid).subscribe({
          next: () => {
            this.messageService.add({ severity: 'success', detail: 'Kursant został usunięty z grupy.' });
            this.refresh();
          },
          error: (error: HttpErrorResponse) =>
            this.showError(this.readApiError(error, 'Nie udało się usunąć kursanta z grupy.')),
        }),
    });
  }

  private openFormDialog(group: IMyOskLectureGroupVm, readonly: boolean): void {
    this.ref = this.primengDialogService.open(LectureGroupFormComponent, {
      ...centerDynamicModalConfig,
      width: '966px',
      data: { group, readonly },
    });
    this.ref.onClose.subscribe((result) => {
      if (result === eModalRoles.SUCCESS || result === eModalRoles.REFRESH) {
        this.refresh();
      }
    });
  }

  private loadMembers(groupGuid: string): void {
    this.membersLoading[groupGuid] = true;
    this.myOskLectureGroupMembersService
      .get(groupGuid)
      .pipe(finalize(() => (this.membersLoading[groupGuid] = false)))
      .subscribe({
        next: (members) => (this.membersByGroup[groupGuid] = members),
        error: () => this.showError('Nie udało się pobrać kursantów grupy.'),
      });
  }

  private readApiError(error: HttpErrorResponse, fallback: string): string {
    const apiMessage =
      typeof error.error === 'string' ? error.error : error.error?.message ?? error.error?.title;
    if (apiMessage?.includes('Lectures are currently running')) {
      return 'Dla tej grupy trwają właśnie wykłady. Zakończ sesję na stanowisku i spróbuj ponownie.';
    }
    if (apiMessage?.includes('archived group')) {
      return 'Nie można dodawać kursantów do zarchiwizowanej grupy.';
    }
    return fallback;
  }

  private showError(detail: string): void {
    this.messageService.add({ severity: 'error', summary: 'Błąd', detail });
  }
}
